// Achievement toast: top-centre DOM overlay (below the dev banner's slot)
// announcing a newly earned achievement or a /give rank|achieve delivery.
// FIFO, one at a time, TOAST_MS each — same split as messageBanner: pure
// queue for unit tests, DOM sink reusing the bundled atlas art as <img> URLs.
import './achievementToast.css';
import type { StorageLike } from '../account/saveSync';
import { sweepAchievements } from '../services/achievementSweep';

export const TOAST_MS = 7_000;

export interface ToastContent {
  /** Line above the icon (the achievement's name, or the award headline). */
  title: string;
  /** Body line beside the icon (how you got it / the achievement's name). */
  body: string;
  /** Atlas texture key for the icon (e.g. 'ach-shopper', 'medal-gold'). */
  iconKey: string;
  /** Optional short text drawn over the icon (the '?' on the rank medal). */
  overlay?: string;
}

export interface ToastSink {
  show(c: ToastContent): void;
  hide(): void;
}

export class ToastQueue {
  private queue: ToastContent[] = [];
  private showing = false;

  constructor(
    private readonly sink: ToastSink,
    private readonly ms: number = TOAST_MS,
  ) {}

  push(c: ToastContent): void {
    this.queue.push(c);
    if (!this.showing) this.next();
  }

  private next(): void {
    const item = this.queue.shift();
    if (!item) {
      this.showing = false;
      return;
    }
    this.showing = true;
    this.sink.show(item);
    setTimeout(() => {
      this.sink.hide();
      this.next();
    }, this.ms);
  }
}

// ── DOM singleton ───────────────────────────────────────────────────────────
// Same bundling mechanism as devPopup: the glob resolves to hashed asset URLs
// at build time, so the DOM shows exactly the game's art.
// The opaque scenery BootScene ships as JPEG is left out: no popup shows it,
// and globbing it here would put every background PNG back into the build.
const ART_URLS = import.meta.glob(
  [
    '/assets/atlas/*.png',
    '!/assets/atlas/bg-*.png',
    '!/assets/atlas/splash-hambi.png',
    '!/assets/atlas/splash-nugget.png',
    '!/assets/atlas/thumb-*.png',
    '!/assets/atlas/origin-*.png',
  ],
  { eager: true, query: '?url', import: 'default' },
) as Record<string, string>;

let domQueue: ToastQueue | null = null;
let root: HTMLDivElement | null = null;

function domSink(): ToastSink {
  return {
    show(c) {
      root = document.createElement('div');
      root.className = 'ach-toast';
      root.dataset.testid = 'ach-toast';
      const title = document.createElement('div');
      title.className = 'ach-toast-title';
      title.dataset.testid = 'ach-toast-title';
      title.textContent = c.title;
      const row = document.createElement('div');
      row.className = 'ach-toast-row';
      const url = ART_URLS[`/assets/atlas/${c.iconKey}.png`];
      if (url) {
        const iconWrap = document.createElement('span');
        iconWrap.className = 'ach-toast-iconwrap';
        const img = document.createElement('img');
        img.className = 'ach-toast-icon';
        img.alt = '';
        img.src = url;
        iconWrap.append(img);
        if (c.overlay) {
          const over = document.createElement('span');
          over.className = 'ach-toast-overlay';
          over.textContent = c.overlay;
          iconWrap.append(over);
        }
        row.append(iconWrap);
      }
      if (c.body) {
        const body = document.createElement('span');
        body.className = 'ach-toast-body';
        body.dataset.testid = 'ach-toast-body';
        body.textContent = c.body;
        row.append(body);
      }
      root.append(title, row);
      document.body.append(root);
    },
    hide() {
      root?.remove();
      root = null;
    },
  };
}

/** Queue an achievement toast (creates the overlay lazily on first use). */
export function enqueueAchievementToast(c: ToastContent): void {
  domQueue ??= new ToastQueue(domSink());
  domQueue.push(c);
}

/**
 * Run the state-based achievement sweep and toast anything newly earned.
 * Safe at any checkpoint: sweep and award are idempotent, and any failure is
 * a warn — achievements must never break the calling scene.
 */
export function sweepAndToastAchievements(storage: StorageLike): void {
  try {
    for (const a of sweepAchievements(storage)) {
      enqueueAchievementToast({ title: a.name, body: a.how, iconKey: a.icon });
    }
  } catch (err) {
    console.warn('[achievements] sweep failed', err);
  }
}
